import React, { useState } from 'react';
import { Upload, Button, Card, Typography, Alert, Spin, App } from 'antd';
import { UploadOutlined, FileTextOutlined, InfoCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import userService from '../services/userService';

const { Title, Text, Paragraph } = Typography;
const { Dragger } = Upload;

const CSVUpload = ({ businessUnitId, businessUnitName, onSuccess }) => {
  const { message, modal } = App.useApp();
  const [uploading, setUploading] = useState(false);
  const [fileList, setFileList] = useState([]);

  const beforeUpload = (file) => {
    const isCSV = file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv');
    if (!isCSV) {
      message.error('You can only upload CSV files!');
      return false;
    }
    
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('File must be smaller than 5MB!');
      return false;
    }
    
    setFileList([file]);
    return false; // Prevent auto upload
  };

  const submitUpload = async (moveExisting) => {
    const formData = new FormData();
    formData.append('csvFile', fileList[0]);
    formData.append('businessUnitId', businessUnitId);
    formData.append('moveExisting', moveExisting ? 'true' : 'false');

    try {
      setUploading(true);
      const result = await userService.uploadParticipantsCSV(formData);

      if (result.success) {
        message.success(result.message);
        setFileList([]);
        if (onSuccess) {
          onSuccess();
        }
      } else {
        message.error(result.message || 'Upload failed');
      }
    } catch (error) {
      console.error('CSV upload error:', error);
      message.error('Failed to upload CSV file');
    } finally {
      setUploading(false);
    }
  };

  const handleUpload = () => {
    if (fileList.length === 0) {
      message.error('Please select a CSV file first');
      return;
    }

    if (!businessUnitId) {
      message.error('Business unit not specified');
      return;
    }

    modal.confirm({
      title: 'Move existing participants?',
      icon: <ExclamationCircleOutlined />,
      content: `Some people in this file may already exist in the system under a different business unit or department. Do you want to automatically move them to ${businessUnitName} and update their department to match this file? If you choose "No", those rows will be skipped and reported as failed instead.`,
      okText: 'Yes, move them',
      cancelText: 'No, skip duplicates',
      onOk: () => submitUpload(true),
      onCancel: () => submitUpload(false),
    });
  };

  const handleRemove = () => {
    setFileList([]);
  };

  const csvFormat = ``;

  return (
    <Card>
      <div style={{ marginBottom: '20px' }}>
        <Title level={4}>
          <FileTextOutlined /> Upload Participants CSV
        </Title>
        <Text type="secondary">
          Upload a CSV file to bulk import participants for {businessUnitName}
        </Text>
      </div>

      <Alert
        icon={<InfoCircleOutlined />}
        message="CSV Format Requirements"
        description={
          <div>
            <Paragraph>
              Your CSV file should include the following columns (header row required):
            </Paragraph>
            <ul>
              <li><strong>name</strong> - First name (required)</li>
              <li><strong>surname</strong> - Last name (required)</li>
              <li><strong>email</strong> - Email address (optional if phone provided, must be unique)</li>
              <li><strong>phone</strong> - Phone number (optional if email provided, supports various formats)</li>
              <li><strong>department</strong> - Department name (required, new departments will be created automatically)</li>
              <li><strong>organization</strong> - Dealership/branch name (optional)</li>
              <li><strong>group</strong> - Dealer group name, e.g. McCarthy, Motus (optional)</li>
            </ul>
            <Paragraph type="warning" style={{ marginTop: '10px' }}>
              <strong>Note:</strong> At least one contact method (email or phone) is required per participant.
            </Paragraph>
            <Paragraph style={{ marginTop: '10px' }}>
              <strong>Phone Number Formats Supported:</strong>
              <br />• With country code: +27821234567, +1234567890
              <br />• Without country code: 0821234567, 821234567 (will default to +27)
              <br />• 10-digit numbers starting with 0 will have the 0 removed
            </Paragraph>
          </div>
        }
        type="info"
        style={{ marginBottom: '20px' }}
        showIcon
      />

   

      <Dragger
        beforeUpload={beforeUpload}
        onRemove={handleRemove}
        fileList={fileList}
        accept=".csv"
        multiple={false}
        style={{ marginBottom: '20px' }}
      >
        <p className="ant-upload-drag-icon">
          <UploadOutlined />
        </p>
        <p className="ant-upload-text">
          Click or drag CSV file to this area to upload
        </p>
        <p className="ant-upload-hint">
          Only CSV files are supported. Maximum file size: 5MB
        </p>
      </Dragger>

      <div style={{ textAlign: 'center' }}>
        <Button
         
          onClick={handleUpload}
          disabled={fileList.length === 0}
          loading={uploading}
          size="large"
          icon={<UploadOutlined />}
        >
          {uploading ? 'Processing...' : 'Upload CSV'}
        </Button>
      </div>

      {uploading && (
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <Spin />
          <div style={{ marginTop: '10px' }}>
            <Text type="secondary">
              Processing CSV file... You will receive an email with the results.
            </Text>
          </div>
        </div>
      )}
    </Card>
  );
};

export default CSVUpload;